import React, { useState } from "react";
import { Search, ExternalLink, Loader, Building2, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * "Find jobs for this role" button + inline results panel.
 *
 * Calls POST /api/jobs-search with the adjacent role title, then renders up
 * to 3 Adzuna listings under the row. Each listing is clickable — it routes
 * to /feed-job with prefilled Job Description so the user can tailor their
 * resume in one click (same UX as the dashboard Insights panel).
 */
export default function AdjacentRoleJobsPanel({ role, location, country, testidPrefix }) {
  const navigate = useNavigate();
  const [state, setState] = useState("idle"); // idle | loading | open | error
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState("");

  const fetchJobs = async () => {
    if (state === "loading") return;
    if (state === "open") {
      setState("idle");
      return;
    }
    setState("loading");
    setError("");
    try {
      const { data } = await api.post("/jobs-search", {
        role, location: location || "", country: country || "us",
      });
      setJobs(Array.isArray(data?.listings) ? data.listings.slice(0, 3) : []);
      setState("open");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setState("error");
    }
  };

  const tailorFromJob = (j) => {
    const hasDesc = (j.description || "").trim().length >= 50;
    if (!hasDesc) {
      if (j.url) window.open(j.url, "_blank", "noopener,noreferrer");
      return;
    }
    navigate("/feed-job", {
      state: {
        prefill: {
          jobDescription: j.description || "",
          jobTitle: j.title || "",
          companyName: j.company || "",
          source: "adzuna",
          adzuna_url: j.url || "",
        },
      },
    });
  };

  return (
    <div className="mt-2" data-testid={`${testidPrefix}-jobs-panel`}>
      <button
        type="button"
        onClick={fetchJobs}
        disabled={state === "loading"}
        className="rm-btn rm-btn-secondary text-[11px] px-3 py-1.5"
        style={{
          background: state === "open" ? "#0F1B2D" : "white",
          color: state === "open" ? "#FFC145" : "#0F1B2D",
          border: "2px solid #0F1B2D",
          fontWeight: 700,
        }}
        data-testid={`${testidPrefix}-jobs-btn`}
      >
        {state === "loading" ? <Loader size={11} className="animate-spin" /> : <Search size={11} />}
        {state === "loading"
          ? "Searching…"
          : state === "open"
            ? "Hide jobs"
            : "Find jobs for this role"}
        {state === "open" ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>

      {state === "open" && (
        <div
          className="mt-2 p-3 space-y-2"
          style={{ background: "#FFFBEB", border: "2px dashed #0F1B2D", borderRadius: 8 }}
          data-testid={`${testidPrefix}-jobs-list`}
        >
          {jobs.length === 0 ? (
            <div className="font-mono-rm text-[11px]" style={{ color: "#475569" }}>
              No live listings found. Try the regional Job Search page.
            </div>
          ) : (
            jobs.map((j, i) => {
              const hasDesc = (j.description || "").trim().length >= 50;
              return (
                <div
                  key={j.id || i}
                  className="flex items-start gap-2 p-2 rounded transition-colors hover:bg-white cursor-pointer"
                  onClick={() => tailorFromJob(j)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); tailorFromJob(j); } }}
                  data-testid={`${testidPrefix}-job-${i}`}
                  title={hasDesc ? "Click to tailor your resume to this job" : "Open at source"}
                >
                  <Building2 size={12} className="flex-shrink-0 mt-0.5" style={{ color: "#0F1B2D" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold leading-tight truncate" style={{ color: "#0F1B2D" }}>
                      {j.title}
                    </div>
                    <div className="text-[11px]" style={{ color: "#475569" }}>
                      {j.company || "—"}{j.location ? ` · ${j.location}` : ""}
                      {(j.salary_min || j.salary_max) && (
                        <> · ${Math.round((j.salary_min || j.salary_max) / 1000)}k{j.salary_max && j.salary_min ? `–${Math.round(j.salary_max / 1000)}k` : ""}</>
                      )}
                    </div>
                    {hasDesc && (
                      <div
                        className="font-mono-rm text-[9px] uppercase tracking-widest mt-1 inline-flex items-center gap-1"
                        style={{ color: "#B91C1C", fontWeight: 700 }}
                      >
                        Tailor my resume → <ArrowRight size={9} />
                      </div>
                    )}
                  </div>
                  {j.url && (
                    <a
                      href={j.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: "#FF6A00" }}
                      title="Open at source"
                    >
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              );
            })
          )}
          <div className="text-center pt-1">
            <button
              type="button"
              onClick={() => navigate(`/job-search?role=${encodeURIComponent(role)}`)}
              className="font-mono-rm text-[10px] uppercase tracking-widest underline"
              style={{ color: "#0F1B2D" }}
              data-testid={`${testidPrefix}-jobs-see-all`}
            >
              See all matches in Job Search →
            </button>
          </div>
        </div>
      )}

      {state === "error" && (
        <div
          className="mt-2 font-mono-rm text-[11px] p-2"
          style={{ background: "#FEF2F2", color: "#991B1B", border: "2px dashed #EF4444", borderRadius: 6 }}
          data-testid={`${testidPrefix}-jobs-error`}
        >
          {error || "Couldn't load jobs. Try the full Job Search page."}
        </div>
      )}
    </div>
  );
}
